import { Button } from '../Button';
import { Skeleton } from '../Skeleton';

/**
 * Pagination footer — reference §3: `1–30 of N`, prev / numbered / next.
 */
export function IndexPagination({
  module,
  page,
  pageCount,
  firstRow,
  lastRow,
  total,
  loading,
  onPageChange,
}: {
  module: string;
  page: number;
  pageCount: number;
  firstRow: number;
  lastRow: number;
  total: number;
  loading: boolean;
  onPageChange: (page: number) => void;
}) {
  return (
    <nav
      aria-label="Pagination"
      className="flex shrink-0 items-center justify-between border-t border-slate-200 px-4 py-2.5"
    >
      <span className="font-mono tabular-nums text-xs text-slate-500">
        {loading ? <Skeleton className="h-3.5 w-24" /> : `${firstRow}–${lastRow} of ${total}`}
      </span>

      <div className="flex items-center gap-1">
        <Button
          id={`${module}-prev-page`}
          variant="secondary"
          disabled={page <= 1 || loading}
          onClick={() => onPageChange(page - 1)}
          className="px-3"
        >
          Previous
        </Button>

        {pageNumbers(page, pageCount).map((entry, index) =>
          entry === null ? (
            <span key={`gap-${index}`} className="px-1.5 text-xs text-slate-400" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              id={`${module}-page-${entry}`}
              onClick={() => onPageChange(entry)}
              aria-current={entry === page ? 'page' : undefined}
              disabled={loading}
              /*
                The current page is the selected thing on this strip, so it gets the
                product's selection treatment and not the brand fill: a blue-50 tint with
                `text-slate-900` on it — CLAUDE.md, "the selected thing is black". Brand
                yellow behind white was both the least readable pair in the product (2.16:1)
                and a second meaning for a colour that elsewhere says "primary action".
              */
              /*
                The current page is OUTLINED as well as tinted — `border-slate-400`, a step
                darker than the Previous/Next buttons flanking it, so the one number that is
                a state rather than a control is the most defined thing on the strip. A tint
                alone left it looking like an unpainted gap between two bordered buttons.

                Unvisited numbers stay borderless: outlining all of them would turn the strip
                into a row of identical buttons with nothing marking where you are.
              */
              className={[
                'min-h-8 min-w-8 rounded-md px-2 font-mono tabular-nums text-xs transition',
                entry === page
                  ? 'border border-slate-400 bg-blue-50 font-medium text-slate-900'
                  : 'border border-transparent text-slate-600 hover:bg-slate-100',
              ].join(' ')}
            >
              {entry}
            </button>
          ),
        )}

        <Button
          id={`${module}-next-page`}
          variant="secondary"
          disabled={page >= pageCount || loading}
          onClick={() => onPageChange(page + 1)}
          className="px-3"
        >
          Next
        </Button>
      </div>
    </nav>
  );
}

/**
 * Windowed page numbers with ellipses, so a 400-page list does not render 400 buttons.
 * `null` marks a gap.
 */
function pageNumbers(current: number, total: number): (number | null)[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | null)[] = [1];

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) pages.push(null);

  for (let i = start; i <= end; i++) pages.push(i);

  if (end < total - 1) pages.push(null);

  pages.push(total);

  return pages;
}
